import React from 'react';
import { CellPlugin } from '@react-page/editor';
import ImageIcon from '@mui/icons-material/Image'; // optional MUI icon

/**
 * Extend Record<string, unknown> so it meets React Page's
 * constraint for plugin data (DataTType).
 */
export interface CustomImageData extends Record<string, unknown> {
  url?: string;
  alt?: string;   // optional alt text
}

/**
 * An async function you provide to upload a selected image to Django.
 * It returns `{ url: string }` – the final public URL of the uploaded file.
 */
export type ImageUploadFunction = (file: File) => Promise<{ url: string }>;

/**
 * Creates a custom "image" plugin for React Page.
 */
export function createCustomImagePlugin(
  uploadImage?: ImageUploadFunction
): CellPlugin<CustomImageData> {
  return {
    id: 'custom-image-plugin',
    title: 'Image',
    description: 'Upload an image and embed it in the page',
    icon: <ImageIcon />, // visually represent in the toolbar
    version: 1,

    // Renderer => how it appears in view-only mode:
    Renderer: ({ data }) => {
      if (!data.url) {
        return <em>No image selected</em>;
      }
      return (
        <img
          src={data.url as string}
          alt={data.alt ?? ''}
          style={{ maxWidth: '100%' }}
        />
      );
    },

    // controls => how it appears/behaves in edit mode
    controls: {
      type: 'custom',
      Component: ({ data, onChange }) => {
        // Handler triggered when user picks a file from <input type="file" />
        const handleFileChange = async (
          e: React.ChangeEvent<HTMLInputElement>
        ) => {
          const file = e.target.files?.[0];
          if (!file || !uploadImage) return;

          // 1) Upload the file to your Django endpoint
          const { url } = await uploadImage(file);
          // 2) Update plugin data with the new image URL
          onChange({ ...data, url, alt: file.name });
        };

        return (
          <div style={{ padding: 10 }}>
            {data.url ? (
              <div>
                <img
                  src={data.url as string}
                  alt={data.alt ?? ''}
                  style={{ maxWidth: '100%', marginBottom: 8 }}
                />
                <br />
                <strong>Image URL:</strong> {data.url}
              </div>
            ) : (
              <em>No image uploaded yet</em>
            )}
            <br />
            <input type="file" accept="image/*" onChange={handleFileChange} />
          </div>
        );
      },
    },
  };
}
